import { useNavigate } from '@tanstack/react-router'
import type { Notification } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { internalPath } from '@/shared/lib/internal-path'
import { notificationColor } from '../model/colors'
import s from './NotificationRow.module.css'

type NotificationRowProps = {
  notification: Notification
  /** Закрыть поповер сверху — только если строка кликабельна и клик состоялся. */
  onNavigate?: () => void
}

/**
 * Строка ленты уведомлений (prototype/Prototype.dc.html:187-201). Прочитанные (`read: true`)
 * приглушены — своё решение фронта, не из макета: в статичном примере макета все три строки
 * непрочитанные («3 новых» = все три), а `Notification.read` в контракте есть и должен на
 * что-то влиять визуально, иначе координаторская фраза «подсветка новых» (Task 16, поправка 2)
 * не отличалась бы от одного только счётчика в шапке поповера.
 *
 * `href` — кнопка с программной навигацией, не `<Link to>`: `href` приходит рантайм-строкой,
 * а не литералом дерева роутов, и `<Link>` в этой версии роутера требует `to` даже при
 * переданном `href` (проверено: `tsc` требует `to` без него). `useNavigate().navigate({ href })`
 * — тот же приём, что уже проверен живьём в `pages/login/ui/LoginPage.tsx` для точно такого же
 * случая (готовая строка пути вместо записи из дерева роутов).
 */
export function NotificationRow({ notification, onNavigate }: NotificationRowProps) {
  const navigate = useNavigate()

  const content = (
    <>
      <span
        className={s.dot}
        style={{ background: notificationColor(notification) }}
        aria-hidden="true"
      />
      <span className={s.text}>
        <span className={s.title}>
          {notification.title}

          {/* «Прочитано» закодировано приглушением — то есть ТОЛЬКО ЦВЕТОМ. Для скринридера
              его не существует, и слепой человек не отличит новое уведомление от старого.
              Скрытый текст дописывается к строке. Тот же приём, что у точки непрочитанных
              чатов в навигации: индикатор, видимый глазом, обязан быть слышим. */}
          {!notification.read && <span className={s.srOnly}>, новое</span>}
        </span>
        <span className={s.description}>{notification.description}</span>
      </span>
      <span className={s.time}>{notification.agoLabel}</span>
    </>
  )

  const rootClassName = cn(s.root, notification.read && s.read)

  /**
   * `href` приходит С СЕРВЕРА, и проверять его обязательно — контракт обещает внутренний путь,
   * но обещание не то же самое, что гарантия.
   *
   * `navigate({ href })` с абсолютным URL роутер выполнит как `window.location.href = …`, то
   * есть уведёт человека из приложения целиком. Prima — платформа из нескольких сервисов, так
   * что абсолютная ссылка в уведомлении — не экзотика, а вопрос времени.
   *
   * Не прошло проверку — строка просто не кликабельна. Уводить в никуда хуже, чем не уводить.
   * Та же `internalPath`, что фильтрует `?redirect=` на входе: одна проверка, два потребителя,
   * разъехаться нечему.
   */
  const href = internalPath(notification.href)

  if (href) {
    return (
      <button
        type="button"
        className={rootClassName}
        onClick={() => {
          onNavigate?.()
          void navigate({ href })
        }}
      >
        {content}
      </button>
    )
  }

  return <div className={cn(rootClassName, s.static)}>{content}</div>
}
